import React, { useEffect, useState } from "react";
import { Loading } from "../Loading/Loading";

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// One row per column: stream type config vs. the stream's table
const buildRows = ({ config, table }) => {
  const names = [...Object.keys(config || {}), ...Object.keys(table || {}).filter((n) => !(n in (config || {})))];
  return names.map((name) => {
    const inConfig = config && name in config;
    const inTable = table && name in table;
    let status = "OK";
    if (!table) status = "—";
    else if (!inConfig) status = "Only in table";
    else if (!inTable) status = "Missing in table";
    else if (config[name] !== table[name]) status = "Type differs";
    return { name, config: inConfig ? config[name] : "—", table: inTable ? table[name] : "—", status };
  });
};

const StreamColumns = ({ result }) => {
  if (result?.error) {
    return <p className="text-red-400">{result.error}</p>;
  }
  const data = result?.data;
  if (!data) return null;
  const rows = buildRows(data);
  return (
    <div className="flex flex-col h-full">
      <div className="text-xs text-gray-300 space-y-1 mb-3">
        <p>Stream type config updated: {formatDate(data.config_updated_at)}</p>
        <p>Stream started / last reset: {formatDate(data.stream_started_at)}</p>
        <p className="break-all">Table: {data.table_name}</p>
        {!data.config && <p className="text-yellow-400">No configuration found for this stream type.</p>}
        {!data.table && <p className="text-yellow-400">The stream's table doesn't exist yet (the stream has not been started).</p>}
        {data.config_changed_after_start && (
          <p className="text-yellow-400">
            The configuration changed after this stream was started: it will be applied at the next reset.
          </p>
        )}
        {data.config && data.table && (
          <p className={data.matches ? "text-green-400 font-bold" : "text-red-400 font-bold"}>
            {data.matches ? "Table matches the configuration" : "Table does NOT match the configuration"}
          </p>
        )}
      </div>
      <div className="overflow-y-auto flex-grow">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-900">
            <tr className="text-left">
              <th className="p-1">Column</th>
              <th className="p-1">Config</th>
              <th className="p-1">Table</th>
              <th className="p-1">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name} className="border-t border-gray-700">
                <td className="p-1 font-mono">{row.name}</td>
                <td className="p-1">{row.config}</td>
                <td className="p-1">{row.table}</td>
                <td className={`p-1 ${row.status === "OK" || row.status === "—" ? "text-green-400" : "text-red-400"}`}>
                  {row.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Columns of a well's streams (one panel per stream type)
const ModalColumnsInformation = ({ isVisible, onClose, onFetchColumns, types }) => {
  const [results, setResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    setIsLoading(true);
    onFetchColumns()
      .then(setResults)
      .finally(() => setIsLoading(false));
    // fetch once each time the modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    isVisible && (
      <div className="bg-gray-800 bg-opacity-75 fixed inset-0 flex items-center justify-center z-[100]">
        <div className="bg-gray-900 p-6 rounded-lg shadow-lg text-white w-[80%] h-[85%] flex flex-col">
          <h2 className="text-xl font-bold mb-4 text-center">Stream Columns</h2>
          <div className="flex-grow bg-gray-800 rounded-lg p-4 flex gap-4 overflow-hidden">
            {types.map((type) => (
              <div key={type} className="flex-1 bg-gray-900 p-3 rounded-lg border border-gray-700 overflow-hidden flex flex-col">
                <h3 className="font-bold text-lg mb-2 text-center">{type.toUpperCase()}</h3>
                <StreamColumns result={results[type]} />
              </div>
            ))}
          </div>
          <button
            onClick={onClose}
            className="mt-4 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg self-center"
          >
            Close
          </button>
        </div>
      </div>
    )
  );
};

export default ModalColumnsInformation;
